import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Group,
  Menu,
  rem,
  ScrollArea,
  Switch,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconBell,
  IconChartBar,
  IconClipboardList,
  IconFileText,
  IconLogout,
  IconSelector,
  IconSettings,
  IconMenu2,
  IconUser,
  IconPhone,
  IconStar,
  IconUserCog,
  IconCoin,
  IconHelp,
  IconLoader,
  IconSteeringWheel,
  IconHistory,
  IconMessage,
  IconMessageCircle,
  IconMessageQuestion,
  IconUserSearch,
  type TablerIcon,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SHELL_NS } from 'recruiter-i18n/namespaces/shell';
import { CORE_NS } from 'recruiter-i18n/namespaces/core';
import { SupportLanguagePicker } from 'components/i18n/SupportLanguagePicker';
import {
  paths,
  useAppNavigate,
  type QueryRecord,
  type StaticAppPathTemplate,
} from 'url-registry';
import { useIsMobile } from 'hooks/useIsMobile';
import { usePermissionsStore } from 'stores/permissionsStore';
import { useSupportUser } from 'hooks/useSupportUser';
import { useSessionVerification } from 'hooks/useSessionVerification';
import { useCalendarIntegrationAutoFetch } from 'hooks/useCalendarIntegration';
import { ensureEnglishCommon } from 'i18n';
import { useFeatureFlagsStore, FeatureFlag } from 'stores/featureFlagsStore';
import env from 'env';
import { reportUnrecoveredChunkError } from 'utils/chunkReload';
import { lazyWithReload } from 'utils/lazyWithReload';
import { clearLocalStorageExcept } from 'utils/storageUtils';
import { devDocsUrl, docsUrl } from 'utils/openDocsPage';
import { notifyError } from 'utils/notify';
import { storage, StorageKey } from 'utils/typedStorage';
import { useStaffingChatStore } from 'stores/staffingChatStore';
import {
  useAlertsPageEnabled,
  useExternalAgentEnabled,
  useMeetingNotesAccess,
} from 'hooks/featureFlags';

import classes from './SideNavBar.module.css';
import GlobalSearch from './GlobalSearch';

// Opened from the account menu only, so it stays out of the app shell. The open
// handler fetches it alongside the screenshot, and the modal renders as soon as
// it opens. Its text is in the candidate `common` bundle it shares with the
// candidate apps.
const loadReportIssueModal = () =>
  Promise.all([
    import('components/ReportIssueModal'),
    ensureEnglishCommon(),
  ]).then(([module]) => module);
const ReportIssueModal = lazyWithReload(loadReportIssueModal);

type NavLabelKey =
  | 'nav.jobs'
  | 'nav.sourcing'
  | 'nav.externalAgent'
  | 'nav.interviews'
  | 'nav.meetingCopilot'
  | 'nav.questions'
  | 'nav.analytics'
  | 'nav.alerts'
  | 'nav.billing'
  | 'nav.auditLog'
  | 'nav.feedback'
  | 'nav.admin';

interface NavItem {
  path: StaticAppPathTemplate;
  /** Stable id for the active state and React keys; translated at render. */
  labelKey: NavLabelKey;
  icon: TablerIcon;
  badge?: string;
  query?: QueryRecord;
}

const COLLAPSE_THRESHOLD = 1400; // Note: threshold here AND css file

interface SideNavBarProps {
  variant?: 'default' | 'drawer';
  onNavigate?: () => void;
}

function DocsButton({ isCollapsed }: { isCollapsed: boolean }) {
  const { t } = useTranslation([SHELL_NS, CORE_NS]);
  const [isLoading, setIsLoading] = useState(false);
  const docsBaseUrl = env.REACT_APP_DOCS_BASE_URL || 'https://docs.tenzo.ai';

  if (!(env.ENV === 'production')) {
    return null;
  }

  const handleOpenDocs = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Lightweight auth request to set session cookie (no full page fetch)
      const response = await fetch(`${docsBaseUrl}/auth`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${storage.get(StorageKey.AuthToken)}`,
        },
        credentials: 'include', // Important: include cookies
      });

      if (response.ok) {
        // Session cookie is now set, open docs in new tab
        window.open(docsUrl('/getting-started/introduction'), '_blank');
      } else {
        notifyError(t('core:status.error'), t('nav.docsOpenFailed'));
      }
    } catch (error) {
      console.error('Error opening docs:', error);
      notifyError(t('core:status.error'), t('nav.docsOpenFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const buttonContent = (
    <button
      onClick={(event) => {
        void handleOpenDocs(event);
      }}
      disabled={isLoading}
      className={classes.docsButton}
      data-collapsed={isCollapsed || undefined}
      data-loading={isLoading || undefined}
    >
      {isLoading ? (
        <IconLoader size={18} className={classes.spin} />
      ) : (
        <IconFileText size={18} />
      )}
      {!isCollapsed && <span>{t('nav.documentation')}</span>}
    </button>
  );

  return isCollapsed ? (
    <Tooltip
      label={t('nav.documentation')}
      position='right'
      withArrow
      transitionProps={{ duration: 0 }}
      offset={10}
    >
      {buttonContent}
    </Tooltip>
  ) : (
    buttonContent
  );
}

function DevDocsButton({ isCollapsed }: { isCollapsed: boolean }) {
  const { t } = useTranslation(SHELL_NS);

  if (!(env.ENV === 'development')) {
    return null;
  }

  const buttonContent = (
    <a
      href={devDocsUrl('/getting-started/introduction')}
      target='_blank'
      rel='noopener noreferrer'
      className={classes.docsButton}
      data-collapsed={isCollapsed || undefined}
    >
      <IconFileText size={18} />
      {!isCollapsed && <span>{t('nav.devDocumentation')}</span>}
    </a>
  );

  return isCollapsed ? (
    <Tooltip
      label={t('nav.devDocumentation')}
      position='right'
      withArrow
      transitionProps={{ duration: 0 }}
      offset={10}
    >
      {buttonContent}
    </Tooltip>
  ) : (
    buttonContent
  );
}

function ChatButton({ isCollapsed }: { isCollapsed: boolean }) {
  const { t } = useTranslation(SHELL_NS);
  const toggleChat = useStaffingChatStore((state) => state.toggle);
  const isChatOpen = useStaffingChatStore((state) => state.isOpen);

  const buttonContent = (
    <button
      // Toggle: clicking again while the popup is open dismisses it.
      onClick={toggleChat}
      aria-pressed={isChatOpen}
      className={classes.chatButton}
      data-collapsed={isCollapsed || undefined}
      data-open={isChatOpen || undefined}
    >
      <IconMessageCircle size={18} />
      {!isCollapsed && <span>{t('nav.assistant')}</span>}
    </button>
  );

  return isCollapsed ? (
    <Tooltip
      label={t('nav.assistant')}
      position='right'
      withArrow
      transitionProps={{ duration: 0 }}
      offset={10}
    >
      {buttonContent}
    </Tooltip>
  ) : (
    buttonContent
  );
}

export default function SideNavBar({
  variant = 'default',
  onNavigate,
}: SideNavBarProps) {
  const { t } = useTranslation([SHELL_NS, CORE_NS]);
  const [active, setActive] = useState<NavLabelKey | ''>('');
  const navigate = useAppNavigate();
  const location = useLocation();
  const firstName = storage.get(StorageKey.FirstName);
  const lastName = storage.get(StorageKey.LastName);
  const email = storage.get(StorageKey.Email) || '';
  const isMobile = useIsMobile();
  const {
    isSupportUser,
    fullAccessEnabled,
    canAccessSupportFeatures,
    toggleFullAccess,
    setSupportUser,
  } = useSupportUser();

  const hasAdmin = usePermissionsStore((state) => state.isAdmin());
  const isPermissionsLoaded = usePermissionsStore((state) => state.isLoaded);
  const isBillingVisible = useFeatureFlagsStore((state) =>
    state.isFeatureEnabled(FeatureFlag.client_billing_page_visible)
  );
  const isMeetingNotesEnabled = useMeetingNotesAccess();
  const isAlertsPageEnabled = useAlertsPageEnabled();
  const isExternalAgentEnabled = useExternalAgentEnabled();
  const openStaffingChat = useStaffingChatStore((state) => state.toggle);
  const isDrawerVariant = variant === 'drawer';

  const [isCollapsed, setIsCollapsed] = useState(
    isDrawerVariant ? false : window.innerWidth <= COLLAPSE_THRESHOLD
  );
  const [isReportIssueModalOpen, setIsReportIssueModalOpen] = useState(false);
  // Mounted on first open and kept, so closing still animates.
  const [hasOpenedReportIssueModal, setHasOpenedReportIssueModal] =
    useState(false);
  const [issueScreenshot, setIssueScreenshot] = useState<string | null>(null);
  const [isTakingScreenshot, setIsTakingScreenshot] = useState(false);

  // Fetch calendar integration once on mount (will be cached globally)
  const calendarState = useCalendarIntegrationAutoFetch();
  const isFetching = calendarState.isLoading;

  useEffect(() => {
    if (isDrawerVariant) {
      setIsCollapsed(false);
      return;
    }

    const handleResize = () => {
      setIsCollapsed(window.innerWidth <= COLLAPSE_THRESHOLD);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial state based on window width

    return () => window.removeEventListener('resize', handleResize);
  }, [isDrawerVariant]);

  const data = useMemo(() => {
    // Full Access mode: support user with Admin permissions
    const isInFullAccessMode = isSupportUser && fullAccessEnabled;
    const inIframe = window.self !== window.top;

    const baseData: NavItem[] = [
      { path: paths.JOBS, labelKey: 'nav.jobs', icon: IconClipboardList },
      { path: paths.SOURCING, labelKey: 'nav.sourcing', icon: IconUserSearch },
      ...(isExternalAgentEnabled && isPermissionsLoaded && hasAdmin
        ? [
            {
              path: paths.EXTERNAL_AGENT,
              labelKey: 'nav.externalAgent' as const,
              icon: IconMessageCircle,
            },
          ]
        : []),
      {
        path: paths.INTERVIEWS,
        labelKey: 'nav.interviews',
        icon: IconUser,
      },
      // Meeting Notes: visible only if feature flag enabled for the org
      ...(isMeetingNotesEnabled
        ? [
            {
              path: paths.NOTES,
              labelKey: 'nav.meetingCopilot' as const,
              icon: IconSteeringWheel,
              query: { tab: 'templates' },
            },
          ]
        : []),
      ...(isPermissionsLoaded && (hasAdmin || isInFullAccessMode)
        ? [
            {
              path: paths.QUESTIONS,
              labelKey: 'nav.questions' as const,
              icon: IconMessageQuestion,
              query: { questionsTab: 'unanswered' },
            },
          ]
        : []),
      {
        path: paths.ANALYTICS,
        labelKey: 'nav.analytics',
        icon: IconChartBar,
      },
      ...(isAlertsPageEnabled
        ? [
            {
              path: paths.ALERTS,
              labelKey: 'nav.alerts' as const,
              icon: IconBell,
            },
          ]
        : []),
      ...(canAccessSupportFeatures || isBillingVisible
        ? [
            {
              path: paths.BILLING,
              labelKey: 'nav.billing' as const,
              icon: IconCoin,
            },
          ]
        : []),
    ];

    // Show Audit Log if user has admin permissions OR if in full access mode
    if (isPermissionsLoaded && (hasAdmin || isInFullAccessMode) && !inIframe) {
      baseData.push({
        path: paths.AUDIT_LOG,
        labelKey: 'nav.auditLog',
        icon: IconHistory,
      });
    }

    // Support-only pages visible in Full Access mode
    if (isInFullAccessMode) {
      baseData.push({
        path: paths.SUPPORT_FEEDBACK,
        labelKey: 'nav.feedback',
        icon: IconStar,
      });
    }

    // Show admin tab if user has admin permissions OR if in full access mode
    if (isPermissionsLoaded && (hasAdmin || isInFullAccessMode) && !inIframe) {
      baseData.push({
        path: paths.SETTINGS,
        labelKey: 'nav.admin',
        icon: IconSettings,
      });
    }

    return baseData;
  }, [
    hasAdmin,
    isPermissionsLoaded,
    isSupportUser,
    fullAccessEnabled,
    canAccessSupportFeatures,
    isBillingVisible,
    isMeetingNotesEnabled,
    isAlertsPageEnabled,
    isExternalAgentEnabled,
  ]);

  // Set active based on the current route
  useEffect(() => {
    const currentPath = location.pathname;
    const currentItem = data.find((item) => {
      const [itemPathname = ''] = item.path.split('?');
      if (
        item.path === paths.NOTES &&
        (currentPath.startsWith(itemPathname) ||
          currentPath.startsWith('/notes'))
      ) {
        return true;
      }
      return currentPath.startsWith(itemPathname);
    });
    if (currentItem) {
      setActive(currentItem.labelKey);
    } else if (currentPath === '/') {
      setActive('nav.jobs');
    }
  }, [location.pathname, data]);

  const navigateToItem = (item: NavItem) => {
    setActive(item.labelKey);
    if (item.path === paths.JOBS) {
      navigate(paths.JOBS, undefined, undefined, {
        state: { resetJobsView: true },
      });
      onNavigate?.();
      return;
    }
    if (item.path === paths.INTERVIEWS) {
      navigate(paths.INTERVIEWS, undefined, undefined, {
        state: { applyInterviewFilterDefaults: true },
      });
      onNavigate?.();
      return;
    }
    navigate(item.path, undefined, item.query);
    onNavigate?.();
  };

  const links = data.map((item) => {
    const linkContent = (
      <UnstyledButton
        type='button'
        w='100%'
        className={classes.link}
        data-active={item.labelKey === active || undefined}
        key={item.labelKey}
        onClick={() => {
          navigateToItem(item);
        }}
      >
        <item.icon className={classes.linkIcon} stroke={1.5} />
        <span>{t(item.labelKey)}</span>
        {item.badge && <span className={classes.badge}>{item.badge}</span>}
      </UnstyledButton>
    );

    return isCollapsed ? (
      <Tooltip
        label={t(item.labelKey)}
        position='right'
        withArrow
        transitionProps={{ duration: 0 }}
        key={item.labelKey}
        offset={10}
      >
        {linkContent}
      </Tooltip>
    ) : (
      linkContent
    );
  });

  const { logout } = useSessionVerification();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // swallow-ok: Logout publishes unauthenticated state even when scoped cleanup fails; finally closes navigation and sends the user to login without clearing the reset-failure latch.
      console.error('Logout cleanup failed; navigating to login:', error);
    } finally {
      onNavigate?.();
      navigate(paths.LOGIN);
    }
  };

  const handleGoToUserSettings = () => {
    navigate(paths.USER_SETTINGS);
    onNavigate?.();
  };

  const handleSwitchUser = () => {
    if (!clearLocalStorageExcept(StorageKey.AuthToken)) {
      notifyError(t('core:status.error'), t('account.storageUnavailable'));
      return;
    }
    setSupportUser();
    // Reload to reset zustand store and re-verify session with fresh state
    window.location.href = paths.LOGIN;
  };

  const handleManagePhoneNumbers = () => {
    navigate(paths.MANAGE_PHONE_NUMBERS, undefined, { tab: 'org_numbers' });
    onNavigate?.();
  };

  const handleToggleFullAccess = () => {
    toggleFullAccess();
    // Reload to apply the change (permissions need to be re-fetched)
    window.location.reload();
  };

  const handleOpenReportIssueModal = async () => {
    try {
      setIsTakingScreenshot(true);
      const [{ takeScreenshot }] = await Promise.all([
        import('utils/imageUtils'),
        loadReportIssueModal(),
      ]);
      const screenshot = await takeScreenshot();
      setIssueScreenshot(screenshot || null);
      setIsReportIssueModalOpen(true);
      setHasOpenedReportIssueModal(true);
    } catch (error) {
      console.error('Error taking screenshot:', error);
      reportUnrecoveredChunkError(error, 'screenshot_chunk_load');
    } finally {
      setIsTakingScreenshot(false);
    }
  };

  return (
    <div className={classes.root}>
      {isMobile && !isDrawerVariant ? (
        <Menu
          shadow='md'
          width={isSupportUser ? 300 : 200}
          zIndex={99999}
          position='bottom-start'
          offset={0}
        >
          <Menu.Target>
            <IconMenu2 className={classes.mobileMenuIcon} />
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>{t('nav.pages')}</Menu.Label>
            {data.map((item) => (
              <Menu.Item
                leftSection={<item.icon />}
                key={item.labelKey}
                onClick={() => {
                  navigateToItem(item);
                }}
              >
                {t(item.labelKey)}
              </Menu.Item>
            ))}
            {!isDrawerVariant && (
              <Menu.Item
                leftSection={
                  <IconMessage style={{ width: rem(14), height: rem(14) }} />
                }
                onClick={openStaffingChat}
              >
                {t('nav.assistant')}
              </Menu.Item>
            )}
            <Menu.Label>{t('account.label')}</Menu.Label>
            {isSupportUser && (
              <Menu.Item
                leftSection={
                  <IconPhone style={{ width: rem(14), height: rem(14) }} />
                }
                onClick={handleManagePhoneNumbers}
              >
                {t('account.orgPhoneNumbers')}
              </Menu.Item>
            )}
            <Menu.Item
              leftSection={<IconSettings />}
              onClick={handleGoToUserSettings}
              disabled={isFetching}
            >
              {t('account.userSettings')}
            </Menu.Item>
            <Menu.Item
              leftSection={
                <IconHelp style={{ width: rem(14), height: rem(14) }} />
              }
              onClick={() => {
                void handleOpenReportIssueModal();
              }}
              disabled={isTakingScreenshot}
            >
              {isTakingScreenshot
                ? t('account.reportIssueLoading')
                : t('account.reportIssue')}
            </Menu.Item>
            {isSupportUser && (
              <Menu.Item
                leftSection={
                  <IconUser style={{ width: rem(14), height: rem(14) }} />
                }
                onClick={handleSwitchUser}
              >
                {t('account.switchUser')}
              </Menu.Item>
            )}
            {isSupportUser && (
              <Menu.Item closeMenuOnClick={false}>
                <Group justify='space-between' wrap='nowrap'>
                  <Group gap='xs' wrap='nowrap'>
                    <IconUserCog style={{ width: rem(14), height: rem(14) }} />
                    <Text size='sm'>{t('account.fullAccess')}</Text>
                  </Group>
                  <Switch
                    checked={fullAccessEnabled}
                    onChange={handleToggleFullAccess}
                    size='sm'
                  />
                </Group>
              </Menu.Item>
            )}
            {isSupportUser && <SupportLanguagePicker />}
            <Menu.Item
              color='red'
              leftSection={<IconLogout />}
              onClick={() => {
                void handleLogout();
              }}
            >
              {t('account.logout')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ) : (
        <nav
          className={`${classes.navbar} ${isDrawerVariant ? classes.drawerNavbar : ''}`}
        >
          <div className={classes.navbarMain}>
            <Group
              className={`${classes.header} ${classes.headerCenter}`}
              justify='space-between'
            >
              <div className={classes.sideNavBarHeader}>
                <img
                  className={classes.full_logo} // Use CSS module class here
                  src='/imgs/tenzo_light_blue_full_3.svg'
                  alt={t('nav.logoAlt')}
                />
                <img
                  className={classes.small_logo} // Use CSS module class here
                  src='/imgs/tenzo_light_blue_just_circle.svg'
                  alt={t('nav.logoAlt')}
                />
              </div>
            </Group>

            {/* Global Search */}
            <div className={classes.searchWrap}>
              <GlobalSearch isNavCollapsed={isCollapsed} />
            </div>

            <ScrollArea className={classes.links}>{links}</ScrollArea>
          </div>

          <div className={classes.footer}>
            <DocsButton isCollapsed={isCollapsed} />
            <DevDocsButton isCollapsed={isCollapsed} />
            {!isDrawerVariant && <ChatButton isCollapsed={isCollapsed} />}
            <Menu
              width={isSupportUser ? 300 : 200}
              position='top-start'
              offset={4}
              zIndex={500}
            >
              <Menu.Target>
                <Group gap='xs' className={classes['user-menu']}>
                  <div className={classes.userRow}>
                    <Avatar
                      color='blue'
                      size='sm'
                      name={`${firstName} ${lastName}`}
                    />
                    <div className={classes.email}>
                      <Text size='xs'>
                        {email && email.length > 13
                          ? `${email.slice(0, 13)}...`
                          : email}
                      </Text>
                    </div>

                    <IconSelector
                      color='var(--salv-dark-5)'
                      stroke={1.2}
                      className={`${classes.icon_selector} ${classes.userSelectorIcon}`}
                    />
                  </div>
                </Group>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{t('account.label')}</Menu.Label>
                {isSupportUser && fullAccessEnabled && (
                  <Menu.Item
                    leftSection={
                      <IconPhone style={{ width: rem(14), height: rem(14) }} />
                    }
                    onClick={handleManagePhoneNumbers}
                  >
                    {t('account.orgPhoneNumbers')}
                  </Menu.Item>
                )}
                <Menu.Item
                  leftSection={
                    <IconSettings style={{ width: rem(14), height: rem(14) }} />
                  }
                  onClick={handleGoToUserSettings}
                  disabled={isFetching}
                >
                  {t('account.userSettings')}
                </Menu.Item>
                <Menu.Item
                  leftSection={
                    <IconHelp style={{ width: rem(14), height: rem(14) }} />
                  }
                  onClick={() => {
                    void handleOpenReportIssueModal();
                  }}
                  disabled={isTakingScreenshot}
                >
                  {isTakingScreenshot
                    ? t('account.reportIssueLoading')
                    : t('account.reportIssue')}
                </Menu.Item>
                {isSupportUser && (
                  <Menu.Item
                    leftSection={
                      <IconUser style={{ width: rem(14), height: rem(14) }} />
                    }
                    onClick={handleSwitchUser}
                  >
                    {t('account.switchUser')}
                  </Menu.Item>
                )}
                {isSupportUser && (
                  <Menu.Item closeMenuOnClick={false}>
                    <Group justify='space-between' wrap='nowrap'>
                      <Group gap='xs' wrap='nowrap'>
                        <IconUserCog
                          style={{ width: rem(14), height: rem(14) }}
                        />
                        <Text size='sm'>{t('account.fullAccess')}</Text>
                      </Group>
                      <Switch
                        checked={fullAccessEnabled}
                        onChange={handleToggleFullAccess}
                        size='sm'
                      />
                    </Group>
                  </Menu.Item>
                )}
                {isSupportUser && <SupportLanguagePicker />}
                <Menu.Item
                  color='red'
                  leftSection={
                    <IconLogout style={{ width: rem(14), height: rem(14) }} />
                  }
                  onClick={() => {
                    void handleLogout();
                  }}
                >
                  {t('account.logout')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        </nav>
      )}
      {hasOpenedReportIssueModal && (
        <React.Suspense fallback={null}>
          <ReportIssueModal
            opened={isReportIssueModalOpen}
            onClose={() => setIsReportIssueModalOpen(false)}
            email={email}
            name={`${firstName} ${lastName}`}
            screenshot={issueScreenshot}
          />
        </React.Suspense>
      )}
    </div>
  );
}
